import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * @returns {string}
   */
  #getShopRoot() {
    const shopify = /** @type {{ routes?: { root?: string } } | undefined} */ (typeof window !== 'undefined' ? window.Shopify : undefined);
    if (shopify?.routes?.root) return shopify.routes.root || '/';
    return '/';
  }

  /**
   * @param {unknown} raw
   * @returns {Record<string, { __MixAndMatchID: string, __MixAndMatchType: string }>}
   */
  #parseMixAndMatchAttribute(raw) {
    if (!raw) return {};
    try {
      const parsed = JSON.parse(String(raw));
      if (!parsed || typeof parsed !== 'object') return {};
      /** @type {Record<string, { __MixAndMatchID: string, __MixAndMatchType: string }>} */
      const output = {};
      Object.entries(parsed).forEach(([key, value]) => {
        if (!value || typeof value !== 'object') return;
        const entry = /** @type {{ id?: unknown, type?: unknown, __MixAndMatchID?: unknown, __MixAndMatchType?: unknown }} */ (value);
        const id = String(entry.__MixAndMatchID || entry.id || '').trim();
        if (!id) return;
        output[key] = {
          __MixAndMatchID: id,
          __MixAndMatchType: String(entry.__MixAndMatchType || entry.type || 'main').trim() || 'main',
        };
      });
      return output;
    } catch (_) {
      return {};
    }
  }

  /**
   * @param {{ items?: Array<{ key?: string, properties?: Record<string, string> }>, attributes?: Record<string, string> }} cart
   * @returns {Record<string, { __MixAndMatchID: string, __MixAndMatchType: string }>}
   */
  #buildMixAndMatchAttributeFromCart(cart) {
    const existing = this.#parseMixAndMatchAttribute(cart?.attributes?.mixandmatch);
    /** @type {Record<string, string>} */
    const existingTypeBySetId = {};
    Object.values(existing).forEach((entry) => {
      if (entry && entry.__MixAndMatchID) existingTypeBySetId[entry.__MixAndMatchID] = entry.__MixAndMatchType || 'main';
    });

    /** @type {string[]} */
    const setIds = [];
    const seen = new Set();
    (cart.items || []).forEach((item) => {
      const setId = item?.properties?.__MixAndMatchID;
      if (!setId || seen.has(setId)) return;
      seen.add(setId);
      setIds.push(setId);
    });

    /** @type {Record<string, { __MixAndMatchID: string, __MixAndMatchType: string }>} */
    const next = {};
    setIds.forEach((setId, index) => {
      const key = String(index + 1).padStart(2, '0');
      next[key] = {
        __MixAndMatchID: setId,
        __MixAndMatchType: existingTypeBySetId[setId] || 'main',
      };
    });
    return next;
  }

  async #syncMixAndMatchCartAttributes() {
    const root = this.#getShopRoot();
    const cart = await fetch(`${root}cart.js`).then((r) => r.json().catch(() => ({})));
    const setMap = this.#buildMixAndMatchAttributeFromCart(cart);
    const firstEntry = Object.values(setMap)[0];
    const attributes = cart && typeof cart.attributes === 'object' ? cart.attributes : {};
    const updateUrl = Theme?.routes?.cart_update_url || `${root}cart/update.js`;

    await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        attributes: {
          ...attributes,
          mixandmatch: JSON.stringify(setMap),
          __MixAndMatchID: firstEntry?.__MixAndMatchID || '',
          __MixAndMatchType: firstEntry?.__MixAndMatchType || '',
        },
      }),
    });
  }

  /**
   * @param {number} line
   */
  #getRowByLineIndex(line) {
    const lineString = String(line);
    return this.refs.cartItemRows.find((row) => row.dataset.lineIndex === lineString) || this.refs.cartItemRows[line - 1];
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('click', this.#handleRemoveAllUnavailable);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#handleRemoveAllUnavailable);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getRowByLineIndex(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * @param {number} line
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.#getRowByLineIndex(line);
    if (!cartItemRowToRemove) return;

    const mixMatchId = cartItemRowToRemove.dataset.mixAndMatchId;
    if (mixMatchId) {
      const groupedLineIndices = (cartItemRowToRemove.dataset.mixAndMatchLines || '')
        .split(',')
        .map((value) => parseInt(value.trim(), 10))
        .filter((n) => !Number.isNaN(n));
      const rowBasedLineIndices = this.refs.cartItemRows
        .filter((row) => row.dataset.mixAndMatchId === mixMatchId)
        .map((row) => parseInt(row.dataset.lineIndex ?? '0', 10))
        .filter((n) => !Number.isNaN(n));
      const setLineIndices = groupedLineIndices.length > 0 ? groupedLineIndices : rowBasedLineIndices;
      if (setLineIndices.length > 0) {
        const rowsToRemove = this.refs.cartItemRows.filter((row) => row.dataset.mixAndMatchId === mixMatchId);
        rowsToRemove.forEach((row) => {
          row.style.setProperty('--row-height', `${row.clientHeight}px`);
          row.classList.add('removing');
        });
        return this.#removeMixAndMatchSet(setLineIndices, mixMatchId);
      }
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      onAnimationEnd(row, remove);
    });
  }

  /**
   * @param {number[]} lineIndices
   * @param {string} [mixMatchId]
   */
  async #removeMixAndMatchSet(lineIndices, mixMatchId = '') {
    let sorted = [...lineIndices].sort((a, b) => b - a);
    const cartPerformanceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');
    this.#disableCartItems();
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sectionsParam = Array.from(sectionsToUpdate).join(',');
    let lastSections = null;

    try {
      if (mixMatchId) {
        const root = this.#getShopRoot();
        const cart = await fetch(`${root}cart.js`).then((r) => r.json().catch(() => ({})));
        const liveLines = (cart.items || [])
          .map((/** @type {{ properties?: Record<string, string> }} */ item, /** @type {number} */ idx) => ({ line: idx + 1, setId: item?.properties?.__MixAndMatchID }))
          .filter((/** @type {{ line: number, setId?: string }} */ entry) => entry.setId === mixMatchId)
          .map((/** @type {{ line: number }} */ entry) => entry.line)
          .sort((/** @type {number} */ a, /** @type {number} */ b) => b - a);
        if (liveLines.length > 0) sorted = liveLines;
      }

      for (const line of sorted) {
        const body = JSON.stringify({
          line,
          quantity: 0,
          sections: sectionsParam,
          sections_url: window.location.pathname,
        });
        const res = await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body,
        });
        const text = await res.text();
        const parsed = JSON.parse(text);
        if (parsed.sections) lastSections = parsed.sections;
        if (parsed.errors) {
          this.#enableCartItems();
          return;
        }
      }

      try {
        await this.#syncMixAndMatchCartAttributes();
      } catch (_) {}
      resetShimmer(this);
      if (lastSections?.[this.sectionId]) {
        const newSectionHTML = new DOMParser().parseFromString(lastSections[this.sectionId], 'text/html');
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: lastSections,
          })
        );
        morphSection(this.sectionId, lastSections[this.sectionId]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformanceUpdateMarker);
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();
    document.documentElement.setAttribute('data-cart-qty-updating', '');

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        await this.#syncMixAndMatchCartAttributes();

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        document.documentElement.removeAttribute('data-cart-qty-updating');
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles Remove All (unavailable items) button click.
   * Removes unavailable line items in descending index order so indices stay valid.
   * @param {Event} event - The click event.
   */
  #handleRemoveAllUnavailable = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target ? target.closest('[data-action="remove-all-unavailable"]') : null;
    if (!button || !(button instanceof HTMLButtonElement)) return;

    const raw = button.dataset.lineIndexes;
    if (!raw) return;

    const indexes = raw
      .split(',')
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => b - a);

    if (indexes.length === 0) return;

    button.disabled = true;
    for (const line of indexes) {
      await this.updateQuantity({ line, quantity: 0, action: 'clear' });
    }
    button.disabled = false;
  };

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      const cartItemsHtml = event.detail.resource?.sections?.[this.sectionId];
      if (cartItemsHtml) {
        morphSection(this.sectionId, cartItemsHtml);
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
